import { TruckRecordingMipEvaluationDTO } from '../../api/generated';
import { TruckRecordingMip } from '../../api/queries/truckRecordingMipList';
import { MATERIAL_PREDICTION_CLASSES, camelToSnake } from '../../api/resources/materialClassification.constants';
import { getConfidenceLevel } from '../Prediction/utils';
import { ProbabilityRange } from '../Prediction/types';

export interface TruckRecordingMaterialPrediction {
  className: (typeof MATERIAL_PREDICTION_CLASSES)[number];
  probability: number;
  confidence: ProbabilityRange;
}

export function getTruckRecordingMaterialPrediction(
  recording: Pick<TruckRecordingMip, 'report'> | undefined
): TruckRecordingMaterialPrediction | null {
  const evaluation = selectMaterialEvaluation(recording?.report?.evaluations);
  if (!evaluation || !evaluation.data || typeof evaluation.data !== 'object') {
    return null;
  }

  const prediction = Object.entries(evaluation.data)
    .filter(([className, probability]) => {
      const normalizedClassName = camelToSnake(className);
      return (
        MATERIAL_PREDICTION_CLASSES.includes(normalizedClassName as (typeof MATERIAL_PREDICTION_CLASSES)[number]) &&
        typeof probability === 'number' &&
        Number.isFinite(probability) &&
        probability >= 0 &&
        probability <= 1
      );
    })
    .map(([className, probability]) => ({
      className: camelToSnake(className) as (typeof MATERIAL_PREDICTION_CLASSES)[number],
      probability: probability as number,
    }))
    .reduce<{ className: (typeof MATERIAL_PREDICTION_CLASSES)[number]; probability: number } | null>(
      (highest, current) => {
        if (highest === null || current.probability > highest.probability) {
          return current;
        }

        if (current.probability === highest.probability) {
          const currentIndex = MATERIAL_PREDICTION_CLASSES.indexOf(current.className);
          const highestIndex = MATERIAL_PREDICTION_CLASSES.indexOf(highest.className);
          return currentIndex < highestIndex ? current : highest;
        }

        return highest;
      },
      null
    );

  if (!prediction) {
    return null;
  }

  return {
    ...prediction,
    confidence: getConfidenceLevel(prediction.probability),
  };
}

function selectMaterialEvaluation(
  evaluations: TruckRecordingMipEvaluationDTO[] | undefined
): TruckRecordingMipEvaluationDTO | null {
  return (
    evaluations?.find(
      (evaluation) =>
        evaluation.key === 'truckMaterialClassifier' &&
        Number.isFinite(Date.parse(evaluation.processingStatus?.finishedAt || ''))
    ) || null
  );
}
